import {
  Button,
  collapseColumn,
  column,
  Css,
  emptyCell,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  Palette,
  ScrollableContent,
  simpleHeader,
  Tag,
  TagType,
  toggleFilter,
  Tooltip,
  useFilter,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { chipCell, SearchBox } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import { CostCodeBidCoverageAlertType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { ProjectParams } from "src/routes/routesDef";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { foldEnum, queryResult } from "src/utils";
import { TruncatedNameListCell } from "../libraries/plan-package/takeoffs/components/TakeoffsTable/TruncatedNameListCell";
import {
  BidCoveragePage_BidPackageFragment,
  BidCoveragePage_CostCodeBidCoverageFragment,
  useBidCoveragePageQuery,
} from "./BidCoveragePage.generated";
import { BidPackageSource, ProductOfferingsBadge } from "./BidPackagesPage";

export function BidCoveragePage() {
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useBidCoveragePageQuery({ variables: { filter: { project: [projectId] } } });
  return queryResult(query, {
    data: ({ costCodeBidCoverage }) => <BidCoveragePageView costCodeBidCoverage={costCodeBidCoverage} />,
  });
}

type BidCoverageFilter = { showAllCostCodes?: boolean };

type BidCoveragePageViewProps = { costCodeBidCoverage: BidCoveragePage_CostCodeBidCoverageFragment[] };

function BidCoveragePageView(props: BidCoveragePageViewProps) {
  const { costCodeBidCoverage } = props;
  const filterDefs: FilterDefs<BidCoverageFilter> = useMemo(
    () => ({ showAllCostCodes: toggleFilter({ label: "Show All Cost Codes" }) }),
    [],
  );
  const { filter, setFilter } = useFilter({ filterDefs });
  const [searchFilter, setSearchFilter] = useState<string>();
  const columns = useMemo(() => createColumns(), []);
  const filteredCostCodes = useMemo(() => {
    if (filter.showAllCostCodes) return costCodeBidCoverage;
    return costCodeBidCoverage.filter((cc) => cc.alerts.nonEmpty);
  }, [costCodeBidCoverage, filter.showAllCostCodes]);
  const rows = useMemo(() => createRows(filteredCostCodes), [filteredCostCodes]);

  return (
    <div>
      <PageHeader title="Bid Coverage" />
      {costCodeBidCoverage.nonEmpty && <BidCoverageChart costCodeBidCoverage={costCodeBidCoverage} />}
      <TableActions>
        <Filters filter={filter} onChange={setFilter} filterDefs={filterDefs} />
        <SearchBox onSearch={setSearchFilter} updateQueryString={false} />
      </TableActions>
      <ScrollableContent>
        <GridTable
          columns={columns}
          rows={rows}
          filter={searchFilter}
          stickyHeader
          fallbackMessage="There aren't any cost codes to display."
        />
      </ScrollableContent>
    </div>
  );
}

function BidCoverageChart(props: BidCoveragePageViewProps) {
  const { costCodeBidCoverage } = props;
  const { slices, total } = useMemo(() => mapToCoverageChartData(costCodeBidCoverage), [costCodeBidCoverage]);

  return (
    <div css={Css.mb3.w100.$}>
      <div css={Css.p2.bgWhite.br8.bshBasic.w100.$}>
        <div css={Css.df.jcsb.aic.mb2.$}>
          <span css={Css.sm.gray700.$}>Cost Codes</span>
          <span css={Css.xl2.$}>{total}</span>
        </div>
        <div css={Css.df.w100.hPx(12).br4.oh.$}>
          {slices.map((slice) => (
            <div key={slice.id} css={Css.h100.$} style={{ flex: slice.value }}>
              <Tooltip
                title={
                  <div css={Css.df.fdc.gap1.$}>
                    <div css={Css.smSb.$}>
                      {slice.id} ({slice.value})
                    </div>
                    {slice.costCodes.map((name) => (
                      <div key={name}>{name}</div>
                    ))}
                  </div>
                }
              >
                <div css={Css.h100.w100.bgColor(slice.color).$} />
              </Tooltip>
            </div>
          ))}
        </div>
        <div css={Css.df.gap3.mt2.$}>
          {slices.map((slice) => (
            <div key={slice.id} css={Css.df.aic.gap1.sm.$}>
              <div css={Css.h1.w1.br100.bgColor(slice.color).$} />
              <span css={Css.gray700.$}>
                {slice.id} ({slice.value})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

type CoverageSlice = { id: string; value: number; color: string; costCodes: string[] };

function mapToCoverageChartData(costCodeBidCoverage: BidCoveragePage_CostCodeBidCoverageFragment[]) {
  const groups = { covered: [] as string[], warning: [] as string[], error: [] as string[] };
  // The chart measures coverage of the takeoff scope, so cost codes that only have bid packages
  // (i.e. missing takeoffs) are shown in the table but excluded here
  const chartRows = costCodeBidCoverage.filter(
    ({ costCode, alerts }) => costCode && !alerts.some((a) => a.type === CostCodeBidCoverageAlertType.MissingTakeoffs),
  );
  chartRows.forEach(({ costCode, alerts }) => {
    const alertTypes = alerts.map((a) => CostCodeBidCoverageAlertTypeMapper(a.type));
    const severity = alertTypes.includes("error") ? "error" : alertTypes.includes("warning") ? "warning" : "covered";
    groups[severity].push(costCode?.displayName ?? "Missing Cost Code");
  });

  const slices: CoverageSlice[] = [
    { id: "Covered", value: groups.covered.length, color: Palette.Green500, costCodes: groups.covered },
    { id: "Partially Covered", value: groups.warning.length, color: Palette.Yellow500, costCodes: groups.warning },
    { id: "Not Covered", value: groups.error.length, color: Palette.Red500, costCodes: groups.error },
  ].filter((slice) => slice.value > 0);

  return { total: chartRows.length, slices };
}

type HeaderRow = { kind: "header" };
type CostCodeRow = { kind: "costCode"; data: Pick<BidCoveragePage_CostCodeBidCoverageFragment, "costCode" | "alerts"> };
type BidPackageRow = { kind: "package"; data: BidCoveragePage_BidPackageFragment };
type Row = HeaderRow | CostCodeRow | BidPackageRow;

function createRows(costCodeBidCoverage: BidCoveragePage_CostCodeBidCoverageFragment[]): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...costCodeBidCoverage.map(({ costCode, bidPackages, alerts }) => ({
      kind: "costCode" as const,
      id: costCode?.id ?? "nocostcode",
      data: { costCode, alerts },
      children: bidPackages.map((bp) => ({
        kind: "package" as const,
        id: `${costCode?.id ?? "nocostcode"}-${bp.id}`,
        data: bp,
      })),
    })),
  ];
}

function createColumns(): GridColumn<Row>[] {
  return [
    collapseColumn<Row>(),
    column<Row>({
      header: "Name",
      w: 2,
      costCode: ({ costCode, alerts }) => ({
        content: (
          <div css={Css.df.fdr.aic.gap2.$}>
            <span css={Css.mdSb.$}>{costCode?.displayName ?? "Bid Packages without takeoffs"}</span>
            {alerts.map((alert) => (
              <Tooltip key={alert.title} title={alert.tooltip}>
                <Tag text={alert.title} type={CostCodeBidCoverageAlertTypeMapper(alert.type)} />
              </Tooltip>
            ))}
          </div>
        ),
        colspan: 3,
      }),
      package: ({ name, bidPackageGroup, id }) =>
        bidPackageGroup ? (
          <Button
            variant="text"
            label={<TruncatedNameListCell nameList={name.split(", ")} />}
            onClick={createBidPackagesDetailUrl(bidPackageGroup.id, id)}
          />
        ) : (
          name
        ),
    }),
    column<Row>({
      header: "Bid Type",
      costCode: emptyCell,
      package: ({ isPlanBasedV2, isUnitBased }) => {
        if (isPlanBasedV2) return "Plan V2";
        if (isUnitBased) return "Unit";
        return "Plan V1";
      },
      w: "80px",
    }),
    column<Row>({ header: "Cost Type", costCode: emptyCell, package: ({ costType }) => costType.name }),
    column<Row>({
      header: "Product Offerings",
      costCode: emptyCell,
      package: ({ latestVersion, projects }) => ({
        content: <ProductOfferingsBadge latestVersion={latestVersion} projects={projects} />,
      }),
    }),
    column<Row>({
      header: "Status",
      costCode: emptyCell,
      package: ({ status }) => (
        <div css={Css.df.aic.$}>
          <StatusIndicator status={status.code} type="BidPackage" />
          <span css={Css.ml1.$}>{status.name}</span>
        </div>
      ),
    }),
    column<Row>({
      header: "Hierarchy",
      costCode: emptyCell,
      package: ({ projects }) => chipCell(projects.map((p) => p.name)),
    }),
    column<Row>({
      header: "Source",
      costCode: emptyCell,
      package: ({ activeBidders }) => <BidPackageSource bidders={activeBidders} />,
    }),
  ];
}

function CostCodeBidCoverageAlertTypeMapper(type: CostCodeBidCoverageAlertType): TagType {
  return foldEnum(type, {
    [CostCodeBidCoverageAlertType.MissingTakeoffs]: "error",
    [CostCodeBidCoverageAlertType.MissingBidPackages]: "error",
    [CostCodeBidCoverageAlertType.PartialMissingBidPackages]: "warning",
  });
}
